import { FormikErrors, FormikTouched } from "formik";
import { TranslateFn } from "../../utils/types";

/**
 * Validation feedback shared by the vehicle CREATE step and the vehicle EDIT
 * modal.
 *
 * WHY THIS EXISTS (incident STC, 2026-11)
 * ---------------------------------------
 * "J'ai tenté de compléter la base Véhicules, je peux saisir, mais je n'ai pas
 * accès à l'enregistrement."
 *
 * Both forms validated with Yup but displayed NO validation message at all:
 *  - on creation the Save buttons were simply disabled while anything was
 *    invalid, so the user could type everything and never find out what was
 *    missing;
 *  - in the edit modal Formik silently refused to call onSubmit, so clicking
 *    Save did nothing.
 * The Yup messages are therefore i18n KEYS (with an English fallback here),
 * shown under each field once the user has tried to save, and summarised in
 * one toast.
 */
export const VEHICLE_FORM_MESSAGES = {
  required: {
    key: "vehicleForm.errors.required",
    fallback: "This field is required",
  },
  brandFromList: {
    key: "vehicleForm.errors.brandFromList",
    fallback: "Select a brand from the list",
  },
  modelFromList: {
    key: "vehicleForm.errors.modelFromList",
    fallback: "Select a model from the list",
  },
  leasingRequired: {
    key: "vehicleForm.errors.leasingRequired",
    fallback: "Say whether this is a rental or leasing vehicle",
  },
  dateInvalid: {
    key: "vehicleForm.errors.dateInvalid",
    fallback: "Invalid date",
  },
} as const;

export type VehicleFormMessage = keyof typeof VEHICLE_FORM_MESSAGES;

/** Yup message for a rule: the message KEY, resolved at render time. */
export const msg = (name: VehicleFormMessage) => VEHICLE_FORM_MESSAGES[name].key;

const translateMessage = (t: TranslateFn, keyOrText: unknown): string => {
  const text = String(keyOrText ?? "");
  const known = Object.values(VEHICLE_FORM_MESSAGES).find((m) => m.key === text);
  return known ? t(known.key, known.fallback) : text;
};

/**
 * The message to show under `field`, or undefined.
 * Only after the field was touched (or a save was attempted, which touches
 * every field) - a pristine form is not covered in red.
 */
export const fieldError = <T,>(
  t: TranslateFn,
  errors: FormikErrors<T>,
  touched: FormikTouched<T>,
  field: keyof T
): string | undefined => {
  const error = (errors as any)?.[field];
  if (!error || !(touched as any)?.[field]) return undefined;
  return translateMessage(t, typeof error === "string" ? error : VEHICLE_FORM_MESSAGES.required.key);
};

/**
 * One sentence naming the fields that block the save, in form order.
 * `labels` maps each field to its (already translated) label.
 */
export const missingFieldsMessage = <T,>(
  t: TranslateFn,
  errors: FormikErrors<T>,
  labels: Partial<Record<keyof T, string>>
): string => {
  const names = (Object.keys(labels) as (keyof T)[])
    .filter((field) => (errors as any)?.[field])
    .map((field) => labels[field] as string);
  return t(
    "vehicleForm.errors.cannotSave",
    "Cannot save yet - please complete: {{fields}}",
    { fields: names.join(", ") }
  );
};

/** Every field marked touched, so all inline errors appear at once. */
export const touchAll = <T extends object>(values: T): FormikTouched<T> =>
  Object.keys(values).reduce(
    (acc, key) => ({ ...acc, [key]: true }),
    {} as FormikTouched<T>
  );

/**
 * Human message for a failed vehicle save.
 *
 * Order: the legacy errorAddVehicle.<key> catalogue (kept so existing
 * translations keep working) -> the first server-side validation error (the
 * API returns { message, errors: { field: [..] } } on 400) -> the server
 * message -> a generic sentence. Never "undefined": a timeout or a network
 * failure has no `response` at all, and used to toast the word "undefined".
 */
export const vehicleSaveErrorMessage = (t: TranslateFn, err: any): string => {
  const data = err?.response?.data;
  if (!err?.response) {
    return t(
      "vehicleForm.errors.network",
      "The server is not responding. Your entries are kept - please try again."
    );
  }
  const firstFieldError = data?.errors
    ? (Object.values(data.errors as Record<string, unknown>)
        .flat()
        .find((m) => typeof m === "string") as string | undefined)
    : undefined;
  const fallback =
    firstFieldError ||
    (typeof data?.message === "string" && data.message) ||
    t(
      "vehicleForm.errors.saveFailed",
      "Saving failed. Please try again or contact support."
    );
  const key = data?.error_key ?? data?.message;
  return key ? t(`errorAddVehicle.${key}`, fallback) : fallback;
};
